/**
 * Funções de mascaramento de dados sensíveis para LGPD
 * Usadas na página pública de checkout para proteger informações pessoais
 */

/**
 * Mascara o nome, mostrando apenas o primeiro nome e inicial do sobrenome
 * @example "Heitor Fernandes Silva" → "Heitor F."
 * @example "Maria" → "Maria"
 */
export function maskNameLGPD(name: string | null): string {
  if (!name) return '-';
  
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0];
  }
  
  const firstName = parts[0];
  const lastNameInitial = parts[parts.length - 1].charAt(0).toUpperCase();
  
  return `${firstName} ${lastNameInitial}.`;
}

/**
 * Mascara CPF/CNPJ, mostrando apenas os dígitos centrais
 * @example CPF: "12303407489" → "***.034.074-**"
 * @example CNPJ: "12345678000190" → "**.345.678/****-**"
 */
export function maskTaxIdLGPD(taxId: string | null): string {
  if (!taxId) return '-';
  
  const digits = taxId.replace(/\D/g, '');
  
  if (digits.length === 11) {
    // CPF: ***.XXX.XXX-**
    return `***.${digits.substring(3, 6)}.${digits.substring(6, 9)}-**`;
  } else if (digits.length === 14) {
    // CNPJ: **.XXX.XXX/****-**
    return `**.${digits.substring(2, 5)}.${digits.substring(5, 8)}/****-**`;
  }
  
  // Fallback: mascara genérica
  return taxId.substring(0, 3) + '***';
}

/**
 * Mascara email, mostrando apenas os 3 primeiros caracteres + domínio
 * @example "heitor.silva@gmail.com" → "hei***@gmail.com"
 */
export function maskEmailLGPD(email: string | null): string {
  if (!email) return '-';
  
  const atIndex = email.indexOf('@');
  if (atIndex <= 0) return '-';
  
  const localPart = email.substring(0, atIndex);
  const domain = email.substring(atIndex);
  
  const visibleChars = Math.min(3, localPart.length);
  const maskedLocal = localPart.substring(0, visibleChars) + '***';
  
  return maskedLocal + domain;
}

/**
 * Mascara telefone, mostrando apenas DDD e últimos 4 dígitos
 * @example "(81) 99812-6853" → "(81) *****-6853"
 */
export function maskPhoneLGPD(phone: string | null): string {
  if (!phone) return '-';
  
  const digits = phone.replace(/\D/g, '');
  
  if (digits.length === 11) {
    // Celular: (XX) *****-XXXX
    const ddd = digits.substring(0, 2);
    const lastFour = digits.substring(7, 11);
    return `(${ddd}) *****-${lastFour}`;
  } else if (digits.length === 10) {
    // Fixo: (XX) ****-XXXX
    const ddd = digits.substring(0, 2);
    const lastFour = digits.substring(6, 10);
    return `(${ddd}) ****-${lastFour}`;
  }
  
  // Fallback
  return phone.substring(0, 4) + '***';
}
